import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLaunch } from '../contexts/LaunchContext';
import { StoreBadges } from './StoreBadges';
import { PhoneFrame } from './PhoneFrame';
import { screens } from '../data/screens';

/** Playful reminders of what actually goes in a kitty. */
const floatingExpenses = [{
  emoji: '🍺',
  label: 'Drinks',
  amount: '£46.74',
  position: 'left-[-6%] top-[16%]'
}, {
  emoji: '🚕',
  label: 'Taxi',
  amount: '£28.50',
  position: 'left-[-2%] bottom-[16%]'
}, {
  emoji: '🏠',
  label: 'Accommodation',
  amount: '£584.65',
  position: 'right-[-4%] top-[9%]'
}, {
  emoji: '🎟️',
  label: 'Tickets',
  amount: '£125.74',
  position: 'right-[-6%] bottom-[22%]'
}];
export function HeroSection() {
  const {
    isPublicLaunch
  } = useLaunch();
  const reduceMotion = useReducedMotion();
  return <section className="relative overflow-hidden pt-32 pb-16 sm:pt-36 lg:pt-40 lg:pb-24" data-id="element-155" data-mp="3fgi33c8mnuts">
      {/* Warm wash behind the composition */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" data-id="element-156" data-mp="2ff0i13mgl5la">
        <div className="absolute -right-[10%] -top-[15%] h-[65%] w-[60%] rounded-full bg-kitty-yellow-100/50 blur-3xl" data-id="element-157" data-mp="2bdx08bkuozhf" />
      </div>

      <div className="mx-auto max-w-7xl px-6 sm:px-8" data-id="element-158" data-mp="2p6d9hu4chtqs">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-8" data-id="element-159" data-mp="2kelhrg9gdhz2">
          {/* Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-5" data-id="element-160" data-mp="2z3hmp4g7qevg">
            {isPublicLaunch && <p className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-bold text-kitty-dark shadow-sm" data-id="element-161" data-mp="383ef9h5q3xvs">
                <span className="relative flex h-2.5 w-2.5" data-id="element-162" data-mp="1dr7srm7dpuo6">
                  <span aria-hidden="true" className="absolute inline-flex h-full w-full animate-ping rounded-full bg-kitty-green opacity-75" data-id="element-163" data-mp="18rbp5o2lg595" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-kitty-green" data-id="element-164" data-mp="38ummaejtkkq6" />
                </span>
                Out now
              </p>}

            <h1 className="mb-6 text-[2.5rem] font-black leading-[1.05] tracking-tight text-kitty-dark sm:text-5xl xl:text-[3.75rem]" data-id="element-165" data-mp="3irr3s12pzs0n">
              Split expenses.
              <br data-id="element-166" data-mp="3mqzusy6dlbsi" />
              Not friendships.
            </h1>

            <p className="mb-8 max-w-lg text-lg leading-relaxed text-gray-600 lg:text-xl" data-id="element-167" data-mp="3rre56mvg8xtp">
              One place for every group expense, so nobody has to remember who paid, chase their mates or argue over the maths.
            </p>

            {isPublicLaunch && <p className="mb-4 text-lg font-black text-kitty-dark" data-id="element-168" data-mp="abfr0plq82bo">
                Start a kitty, it's free.
              </p>}

            <StoreBadges tone="light" data-id="element-169" data-mp="eec2zxgrh06n" />

            <p className="mt-5 text-sm font-semibold text-gray-500" data-id="element-170" data-mp="eb7rsg4vp46h">
              Unlimited kitties • No subscription • Friends can join without the
              app
            </p>
          </motion.div>

          {/* Product composition */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-7" data-id="element-171" data-mp="1yutm4tm5f49b">
            <div className="relative mx-auto w-full max-w-[300px] sm:max-w-[620px]" data-id="element-172" data-mp="2mknzrdeakwum">
              {/* Behind, left: My Balance */}
              <PhoneFrame screen={screens.myBalance} decorative className="absolute left-0 top-[10%] hidden w-[42%] -rotate-[7deg] sm:block" data-id="element-173" data-mp="2iklwx0wtlxdr" />

              {/* Behind, right: settlement history */}
              <PhoneFrame screen={screens.history} decorative className="absolute right-0 top-[16%] hidden w-[42%] rotate-[7deg] sm:block" data-id="element-174" data-mp="28spxnmj0017y" />

              {/* Front and centre: the kitty itself. Above the fold, so it is
                  deliberately not lazy-loaded. */}
              <div className="relative z-20 mx-auto w-full sm:w-[52%]" data-id="element-175" data-mp="2l25uy5hha07v">
                <PhoneFrame screen={screens.kittyMain} shadow="xl" eager data-id="element-176" data-mp="2l50d1qd0r8y4" />
              </div>

              {/* Floating expenses */}
              {floatingExpenses.map((expense, index) => <motion.div key={expense.label} aria-hidden="true" initial={{
              opacity: 0,
              y: 10
            }} animate={reduceMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 1,
              y: [0, -7, 0]
            }} transition={reduceMotion ? {
              duration: 0.3
            } : {
              opacity: {
                duration: 0.4,
                delay: 0.4 + index * 0.1
              },
              y: {
                duration: 4 + index * 0.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.3
              }
            }} className={`absolute z-30 hidden items-center gap-2.5 rounded-2xl border border-gray-100 bg-white px-3.5 py-2.5 shadow-card lg:flex ${expense.position}`} data-id="element-177" data-mp="2c3tvddymvs3a" data-mpi={index}>
                  <span className="text-lg leading-none" data-id="element-178" data-mp="1obdl6ujbquo">{expense.emoji}</span>
                  <span className="text-sm font-bold text-kitty-dark" data-id="element-179" data-mp="1m8pwtguu9t4">
                    {expense.label}
                  </span>
                  <span className="text-sm font-black text-kitty-dark" data-id="element-180" data-mp="ag3mhd3wurx5">
                    {expense.amount}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}